import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { Check, Bitcoin, CreditCard, Sparkles, Copy, Shield } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { SystemsComparison } from "../components/Guardian";

const PLAN_META = {
  bund_basic_3m: { per: "per_3m" },
  bund_professional_6m: { per: "per_6m", featured: true },
  bund_business_12m: { per: "per_12m" },
};

export function CryptoModal({ plan, onClose }) {
  const { t } = useLang();
  const [wallets, setWallets] = useState([]);
  const [currency, setCurrency] = useState("BTC");
  const [txHash, setTxHash] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/payments/crypto/wallets").then((r) => setWallets(r.data.wallets)); }, []);

  const submit = async () => {
    if (!txHash.trim()) return;
    setBusy(true);
    try {
      await api.post("/payments/crypto/submit", { lookup_key: plan.lookup_key, currency, tx_hash: txHash });
      toast.success(t("crypto_sent"));
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="crypto-modal" className="bg-[#111111] border border-white/15 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-bold text-white">{t("crypto_title")} — {plan.name} €{plan.amount_eur}</h3>
        <p className="text-sm text-slate-400 mt-2">{t("crypto_sub")}</p>
        <div className="space-y-2 mt-4">
          {wallets.map((w) => (
            <button key={w.currency} data-testid={`crypto-wallet-${w.currency}`}
              onClick={() => setCurrency(w.currency)}
              className={`w-full text-left border px-3 py-2 flex items-center gap-3 transition-colors ${currency === w.currency ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 hover:border-white/25"}`}>
              <span className="font-mono text-sm text-amber-400 w-12 shrink-0">{w.currency}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-mono text-xs text-slate-300 truncate">{w.address || "—"}</span>
                <span className="block text-[10px] text-slate-500">{w.network}</span>
              </span>
              <Copy className="w-3.5 h-3.5 text-slate-500" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(w.address); toast.success("Copied"); }} />
            </button>
          ))}
        </div>
        {(() => {
          const sel = wallets.find((w) => w.currency === currency);
          if (!sel?.address) return null;
          return (
            <div data-testid="crypto-selected-qr" className="mt-4 border border-white/10 bg-black/40 p-3 flex items-center gap-4">
              <div className="bg-white p-1.5 shrink-0"><QRCodeCanvas value={sel.address} size={88} /></div>
              <div className="min-w-0">
                <p className="font-mono text-[11px] text-slate-300 break-all">{sel.address}</p>
                <p className="text-[10px] text-amber-400/90 mt-1">{sel.network}</p>
                {sel.memo && <p className="text-[10px] text-slate-500 mt-1">{sel.memo}</p>}
              </div>
            </div>
          );
        })()}
        <input data-testid="crypto-tx-input" type="text" placeholder={t("tx_hash")} value={txHash}
          onChange={(e) => setTxHash(e.target.value)}
          className="w-full mt-4 bg-[#050505] border border-white/15 px-3 py-2.5 text-sm font-mono text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
        <div className="flex gap-3 mt-4">
          <button data-testid="crypto-submit-btn" onClick={submit} disabled={busy || !txHash.trim()}
            className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-medium py-2.5 text-sm transition-colors">{t("submit_tx")}</button>
          <button data-testid="crypto-cancel-btn" onClick={onClose} className="border border-white/15 text-slate-300 px-4 text-sm hover:bg-white/5 transition-colors">{t("cancel")}</button>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, setUser } = useAuth();
  const { lang, t } = useLang();
  const it = lang === "it";
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cryptoPlan, setCryptoPlan] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);
  const alphaPlans = plans.filter((p) => p.product === "alpha");
  const guardianPlan = plans.find((p) => p.product === "guardian");

  const requireLogin = () => { toast.info(t("login_required")); navigate("/login"); };

  const checkout = async (plan) => {
    if (!user) return requireLogin();
    setBusy(plan.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy("");
    }
  };

  const activateTrial = async () => {
    if (!user) return requireLogin();
    try {
      const { data } = await api.post("/subscription/trial");
      setUser(data);
      toast.success(`${t("trial_active")} ${data.trial_expires_at?.slice(0, 10)}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-white">{t("plans_title")}</h1>
      <p className="text-slate-400 mt-3 max-w-xl">
        {it ? "Due sistemi, due offerte separate: scegli il profilo adatto a te." : "Two systems, two separate offers: choose the profile that fits you."}
      </p>

      <div className="mt-12 flex items-center gap-3">
        <span className="font-heading font-bold text-xl text-emerald-400">Bund Alpha</span>
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{it ? "Sistema speculativo · Stop & Reverse" : "Speculative system · Stop & Reverse"}</span>
      </div>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">
        {it
          ? "Sempre a mercato, long o short, per catturare il massimo movimento del Bund. Segnale in tempo reale ogni sera ~22:00."
          : "Always in the market, long or short, to capture the Bund's full movement. Real-time signal every evening ~22:00."}
      </p>

      <div data-testid="trial-banner" className="mt-6 border border-emerald-500/30 bg-emerald-500/5 p-5 flex flex-wrap items-center gap-4">
        <Sparkles className="w-6 h-6 text-emerald-400" />
        <div className="flex-1 min-w-[240px]">
          <p className="font-medium text-emerald-200">{t("trial_title")}</p>
          <p className="text-sm text-slate-400">{t("trial_sub")}</p>
        </div>
        {user?.access_level === "trial" ? (
          <span className="text-sm font-mono text-emerald-400">{t("trial_active")} {user.trial_expires_at?.slice(0, 10)}</span>
        ) : user?.trial_used ? (
          <span data-testid="trial-used-label" className="text-sm text-slate-500">{t("trial_used")}</span>
        ) : (
          <button data-testid="trial-activate-btn" onClick={activateTrial}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-medium px-5 py-2.5 text-sm transition-colors">{t("trial_cta")}</button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {alphaPlans.map((p) => {
          const meta = PLAN_META[p.lookup_key] || {};
          return (
            <div key={p.lookup_key} data-testid={`plan-card-${p.lookup_key}`}
              className={`border p-8 bg-[#111111] transition-transform hover:scale-[1.02] ${meta.featured ? "border-emerald-500/50" : "border-white/10"}`}>
              <p className="text-xs tracking-[0.25em] uppercase text-slate-400">{p.name}</p>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-mono text-4xl text-white">€{p.amount_eur}</span>
                <span className="text-sm text-slate-500">{t(meta.per)}</span>
              </div>
              <ul className="space-y-2.5 mt-6 text-sm text-slate-300">
                {["plan_feat1", "plan_feat2", "plan_feat3", "plan_feat4"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{t(f)}</li>
                ))}
              </ul>
              <button data-testid={`plan-checkout-${p.lookup_key}`} onClick={() => checkout(p)} disabled={busy === p.lookup_key}
                className="w-full mt-8 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-medium py-3 text-sm flex items-center justify-center gap-2 transition-colors">
                <CreditCard className="w-4 h-4" /> {t("pay_card")}
              </button>
              <button data-testid={`plan-crypto-${p.lookup_key}`} onClick={() => (user ? setCryptoPlan(p) : requireLogin())}
                className="w-full mt-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 py-3 text-sm flex items-center justify-center gap-2 transition-colors">
                <Bitcoin className="w-4 h-4" /> {t("pay_crypto")}
              </button>
            </div>
          );
        })}
      </div>

      <div id="guardian" className="mt-16 pt-12 border-t border-white/10">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-sky-400" />
          <span className="font-heading font-bold text-xl text-sky-400">Bund Guardian</span>
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{it ? "Sistema conservativo · Zero anni in perdita" : "Conservative system · Zero losing years"}</span>
        </div>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          {it
            ? "Per l'investitore più conservativo: in 18 anni di serie storica non ha mai chiuso un anno in perdita. Poche operazioni l'anno (anche solo 2), stop loss rigido su ogni trade e profitto annuale blindato dal profit lock."
            : "For the more conservative investor: in 18 years of track record it has never closed a year at a loss. Few trades per year (even just 2), hard stop loss on every trade and yearly profit secured by the profit lock."}
        </p>
        <div className="grid md:grid-cols-2 gap-6 mt-6 items-start">
          {guardianPlan && (
            <div data-testid="plan-card-guardian" className="border border-sky-500/40 bg-[#0B1220] p-8">
              <p className="text-xs tracking-[0.25em] uppercase text-sky-400">{it ? "Guardian — solo annuale" : "Guardian — annual only"}</p>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-mono text-4xl text-white">€{guardianPlan.amount_eur}</span>
                <span className="text-sm text-slate-500">{t("per_12m")}</span>
              </div>
              <ul className="space-y-2.5 mt-6 text-sm text-slate-300">
                {[it ? "Segnale Guardian in tempo reale (~22:00)" : "Real-time Guardian signal (~22:00)",
                  it ? "Operazione in corso in chiaro (SL, TP, ingresso)" : "Ongoing operation in clear (SL, TP, entry)",
                  it ? "Storico completo 2009-2026 trade per trade" : "Full 2009-2026 trade-by-trade history",
                  it ? "Mai un anno chiuso in perdita in 18 anni" : "Never a losing year in 18 years"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <p className="text-xs text-amber-300/80 mt-4">{it ? "Trial non ammesso per questo sistema." : "No trial available for this system."}</p>
              <button data-testid="plan-checkout-bund_guardian_12m" onClick={() => checkout(guardianPlan)} disabled={busy === guardianPlan.lookup_key}
                className="w-full mt-6 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-black font-medium py-3 text-sm flex items-center justify-center gap-2 transition-colors">
                <CreditCard className="w-4 h-4" /> {t("pay_card")}
              </button>
              <button data-testid="plan-crypto-bund_guardian_12m" onClick={() => (user ? setCryptoPlan(guardianPlan) : requireLogin())}
                className="w-full mt-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 py-3 text-sm flex items-center justify-center gap-2 transition-colors">
                <Bitcoin className="w-4 h-4" /> {t("pay_crypto")}
              </button>
              <Link to="/guardian" data-testid="guardian-learn-more" className="block text-center text-sm text-sky-400 hover:text-sky-300 mt-4 underline underline-offset-4">
                {it ? "Scopri il sistema Guardian e le performance complete →" : "Discover the Guardian system and full performance →"}
              </Link>
            </div>
          )}
          <div>
            <h3 className="text-sm font-medium text-white mb-3">{it ? "Confronto tra i due sistemi" : "Comparison between the two systems"}</h3>
            <SystemsComparison />
          </div>
        </div>
      </div>
      {cryptoPlan && <CryptoModal plan={cryptoPlan} onClose={() => setCryptoPlan(null)} />}
    </div>
  );
}
